/**
 * 邀请码有效性校验 —— `GET /auth/validate?invite_code=<code>`（匿名可调）。
 *
 * 后端 `AuthenticationApiDelegateImpl.validate()` 与注册链路**共用 `InviteCodeResolver`**，
 * 所以这里的判定与「注册时能否绑定」完全一致，不会出现「校验说有效、注册却没绑上」。
 *
 * ## 为什么要三态而不是 boolean
 *
 * 该端点有双层限流（设备短窗口 + IP 长窗口）会返回 429，加上网络本身可能失败。
 * **「后端说无效」和「问不到答案」必须分开**：
 * - 判定为 `invalid` → 可以清掉本地码、给用户报错
 * - 判定不了（`unknown`）→ **绝不能当作无效** —— 那会把一个好码丢掉，用户白白损失邀请关系。
 *   此时乐观放行：注册请求仍会带上 `OS-Invite-Code`，后端在注册时还会再判一次，绑不上也只是静默忽略。
 */

/** 最小请求接口 —— 只依赖 `HttpClient.request`，不依赖 gen:api 生成的方法签名。 */
export interface InviteValidationRequester {
  request: (args: {
    path: string;
    method: string;
    query?: Record<string, unknown>;
    /** 字面量而非 string —— 生成的 HttpClient 用的是联合字面量类型，写宽了不兼容。 */
    format?: "json";
  }) => Promise<unknown>;
}

export type InviteValidationStatus = "valid" | "invalid" | "unknown";

export interface InviteValidationResult {
  status: InviteValidationStatus;
  /** 后端在 valid 时回传，用于展示邀请人（当前 Figma 未用，先透出不渲染）。 */
  inviter?: { nickName?: string; avatar?: string };
}

/** 兼容 client 是否解包 BaseResponse 两种形状：`{data:{valid}}` 与 `{valid}`。 */
function readValidation(payload: unknown): {
  valid?: boolean;
  inviter?: { nick_name?: string; avatar?: string };
} | null {
  if (!payload || typeof payload !== "object") {
    return null;
  }
  const outer = payload as Record<string, unknown>;
  // HttpResponse 形状：{ data: <body> }
  const body = (outer.data ?? outer) as Record<string, unknown>;
  if (!body || typeof body !== "object") {
    return null;
  }
  // BaseResponse 形状：{ code, msg, data: { valid, inviter } }
  const inner = (body.data ?? body) as Record<string, unknown>;
  if (!inner || typeof inner !== "object") {
    return null;
  }
  if (typeof inner.valid !== "boolean") {
    return null;
  }
  return inner as {
    valid: boolean;
    inviter?: { nick_name?: string; avatar?: string };
  };
}

/**
 * 校验邀请码。**任何异常都返回 `unknown`，不返回 `invalid`** —— 见文件顶部说明。
 */
export async function validateInviteCode(
  client: InviteValidationRequester | null | undefined,
  code: string,
): Promise<InviteValidationResult> {
  const trimmed = code?.trim();
  if (!trimmed) {
    return { status: "invalid" };
  }
  if (!client?.request) {
    return { status: "unknown" };
  }
  try {
    const raw = await client.request({
      path: "/auth/validate",
      method: "GET",
      query: { invite_code: trimmed },
      format: "json",
    });
    const parsed = readValidation(raw);
    if (!parsed || typeof parsed.valid !== "boolean") {
      // 结构不认识 → 不敢断言无效
      return { status: "unknown" };
    }
    if (!parsed.valid) {
      return { status: "invalid" };
    }
    return {
      status: "valid",
      inviter: parsed.inviter
        ? { nickName: parsed.inviter.nick_name, avatar: parsed.inviter.avatar }
        : undefined,
    };
  } catch {
    // 429 限流 / 网络失败 / 超时 —— 判定不了，乐观放行
    return { status: "unknown" };
  }
}
